// src/components/operationsAssociate/CreateRFQs.jsx
import { useState, useEffect } from "react";
import { createRFQ } from "../../api/OAprocurement-api";
import { getVendors } from "../../api/vendorApi"; // Import getVendors
import { useNavigate } from 'react-router-dom';

const MAX_FILE_SIZE_MB = 5;
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const CreateRFQ = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [rfqTitle, setRfqTitle] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [buyerContact, setBuyerContact] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [submissionDeadline, setSubmissionDeadline] = useState("");
  const [lineItems, setLineItems] = useState([
    { itemCode: "", description: "", quantity: 1, unit: "", remarks: "" },
  ]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // NEW: Vendor selection states
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [vendorError, setVendorError] = useState(null);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  // NEW: Fetch vendors on component mount
  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setVendorError("Authentication token not found. Please log in.");
          return;
        }

        console.log("Fetching vendors for RFQ...");
        const response = await getVendors();
        console.log("Vendors response:", response);
        
        // Handle different possible response structures
        let vendorData = [];
        if (Array.isArray(response)) {
          vendorData = response;
        } else if (response?.data) {
          vendorData = Array.isArray(response.data) ? response.data : response.data.data || [];
        }
        
        console.log("Processed vendor data:", vendorData);
        setVendors(Array.isArray(vendorData) ? vendorData : []);
      } catch (error) {
        console.error("Failed to load vendors:", error);
        setVendorError(`Failed to load vendors: ${error.message}`);
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, []);

  // NEW: Handle vendor selection
  const handleVendorToggle = (vendor) => {
    const isSelected = selectedVendors.some(v => v._id === vendor._id);
    
    if (isSelected) {
      // Remove vendor
      setSelectedVendors(selectedVendors.filter(v => v._id !== vendor._id));
    } else {
      // Add vendor
      setSelectedVendors([...selectedVendors, vendor]);
    }
  };

  // NEW: Select all vendors
  const handleSelectAllVendors = () => {
    if (selectedVendors.length === vendors.length) {
      setSelectedVendors([]); // Deselect all
    } else {
      setSelectedVendors([...vendors]); // Select all
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!rfqTitle.trim()) errors.rfqTitle = "RFQ Title is required.";
    if (!referenceNumber.trim()) errors.referenceNumber = "Reference Number is required.";
    if (!buyerContact.trim()) errors.buyerContact = "Buyer Contact is required.";

    if (!issueDate) errors.issueDate = "Issue Date is required.";
    if (!submissionDeadline) errors.submissionDeadline = "Submission Deadline is required.";
    if (issueDate && submissionDeadline && submissionDeadline <= issueDate)
      errors.submissionDeadline = "Submission Deadline must be after Issue Date.";

    // NEW: Validate vendor selection
    if (selectedVendors.length === 0) {
      errors.vendors = "Please select at least one vendor to notify.";
    }

    lineItems.forEach((item, idx) => {
      if (!item.itemCode.trim()) {
        errors[`itemCode-${idx}`] = "Item Code is required.";
      }
      if (!item.description.trim()) {
        errors[`description-${idx}`] = "Description is required.";
      }
      if (!item.quantity || Number(item.quantity) <= 0) {
        errors[`quantity-${idx}`] = "Quantity must be greater than zero.";
      }
      if (!item.unit.trim()) {
        errors[`unit-${idx}`] = "Unit is required.";
      }
    });

    files.forEach((file, idx) => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors[`fileType-${idx}`] = `File type ${file.type} not allowed.`;
      }
      if (file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
        errors[`fileSize-${idx}`] = `File size exceeds ${MAX_FILE_SIZE_MB}MB.`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index][field] = value;
    setLineItems(updatedItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { itemCode: "", description: "", quantity: 1, unit: "", remarks: "" }]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConfirm(true);
  };

  // UPDATED: Include selected vendors in RFQ data
  const confirmSubmit = async () => {
    setShowConfirm(false);
    setLoading(true);
    try {
      const rfqData = {
        rfqTitle,
        referenceNumber,
        buyerContact,
        issueDate,
        submissionDeadline,
        lineItems,
        files: files.map((file) => file.name),
        // NEW: Include selected vendors
        notifiedVendors: selectedVendors.map(vendor => ({
          vendorId: vendor._id,
          vendorName: vendor.name,
          vendorCode: vendor.vendorCode || vendor.code || '',
          email: vendor.email || vendor.contactEmail,
          contactPerson: vendor.contactPerson || vendor.contactName
        })),
        vendorEmails: selectedVendors.map(vendor => vendor.email || vendor.contactEmail).filter(Boolean)
      };

      console.log("Sending RFQ with vendors:", rfqData);

      await createRFQ(rfqData);
      
      console.log("RFQ created successfully!");
      alert(`RFQ created successfully and sent to ${selectedVendors.length} vendor(s)!`);
      
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error("Failed to create RFQ", error.response?.data || error.message);
      alert("Failed to create RFQ. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow-lg rounded-2xl">
      <h2 className="text-2xl font-bold mb-6 text-gray-700">Create New RFQ</h2>

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* RFQ Metadata */}
        <div>
          <label className="block font-medium mb-1">RFQ Title</label>
          <input
            type="text"
            value={rfqTitle}
            onChange={(e) => setRfqTitle(e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 ${formErrors.rfqTitle ? "border-red-500" : ""}`}
            required
          />
          {formErrors.rfqTitle && <p className="text-red-500 text-sm">{formErrors.rfqTitle}</p>}
        </div>

        <div>
          <label className="block font-medium mb-1">Reference Number</label>
          <input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 ${formErrors.referenceNumber ? "border-red-500" : ""}`}
            required
          />
          {formErrors.referenceNumber && <p className="text-red-500 text-sm">{formErrors.referenceNumber}</p>}
        </div>

        <div>
          <label className="block font-medium mb-1">Buyer Contact</label>
          <input
            type="text"
            value={buyerContact}
            onChange={(e) => setBuyerContact(e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 ${formErrors.buyerContact ? "border-red-500" : ""}`}
            required
          />
          {formErrors.buyerContact && <p className="text-red-500 text-sm">{formErrors.buyerContact}</p>}
        </div>

        {/* Issue Date & Submission Deadline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-1">Issue Date</label>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 ${formErrors.issueDate ? "border-red-500" : ""}`}
              required
            />
            {formErrors.issueDate && <p className="text-red-500 text-sm">{formErrors.issueDate}</p>}
          </div>

          <div>
            <label className="block font-medium mb-1">Submission Deadline</label>
            <input
              type="date"
              value={submissionDeadline}
              onChange={(e) => setSubmissionDeadline(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 ${formErrors.submissionDeadline ? "border-red-500" : ""}`}
              required
            />
            {formErrors.submissionDeadline && <p className="text-red-500 text-sm">{formErrors.submissionDeadline}</p>}
          </div>
        </div>

        {/* NEW: Vendor Selection Section */}
        <div>
          <label className="block font-medium mb-2">Select Vendors to Notify</label>
          
          {vendorError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3">
              {vendorError}
            </div>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVendorDropdown(!showVendorDropdown)}
              className={`w-full border rounded-lg px-3 py-2 text-left bg-white flex justify-between items-center ${formErrors.vendors ? "border-red-500" : "border-gray-300"} ${loadingVendors ? 'cursor-wait' : 'cursor-pointer'}`}
              disabled={loadingVendors}
            >
              <span className="text-gray-700">
                {loadingVendors ? "Loading vendors..." : 
                 selectedVendors.length === 0 ? "Select vendors to notify..." :
                 `${selectedVendors.length} vendor(s) selected`}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showVendorDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showVendorDropdown && !loadingVendors && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {/* Select All Option */}
                <div className="p-2 border-b border-gray-200">
                  <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedVendors.length === vendors.length && vendors.length > 0}
                      onChange={handleSelectAllVendors}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="font-medium text-gray-700">
                      Select All ({vendors.length} vendors)
                    </span>
                  </label>
                </div>

                {/* Vendor List */}
                {vendors.length === 0 ? (
                  <div className="p-4 text-gray-500 text-center">
                    No vendors available
                  </div>
                ) : (
                  vendors.map((vendor) => (
                    <label
                      key={vendor._id}
                      className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedVendors.some(v => v._id === vendor._id)}
                        onChange={() => handleVendorToggle(vendor)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {vendor.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {vendor.vendorCode || vendor.code ? `Code: ${vendor.vendorCode || vendor.code} | ` : ''}
                          {vendor.email || vendor.contactEmail || 'No email'}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {formErrors.vendors && <p className="text-red-500 text-sm mt-1">{formErrors.vendors}</p>}

          {/* Selected Vendors Display */}
          {selectedVendors.length > 0 && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm font-medium text-green-800 mb-2">
                Selected Vendors ({selectedVendors.length}):
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedVendors.map((vendor) => (
                  <span
                    key={vendor._id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {vendor.name}
                    <button
                      type="button"
                      onClick={() => handleVendorToggle(vendor)}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Line Items */}
        <div>
          <label className="block font-medium mb-3">Line Items</label>
          {lineItems.map((item, idx) => (
            <div key={idx} className="grid grid-cols-6 gap-3 mb-3 items-center">
              <input
                type="text"
                placeholder="Item Code"
                value={item.itemCode}
                onChange={(e) => handleLineItemChange(idx, "itemCode", e.target.value)}
                className={`border rounded-lg px-2 py-1 col-span-1 ${formErrors[`itemCode-${idx}`] ? "border-red-500" : ""}`}
                required
              />
              <input
                type="text"
                placeholder="Description"
                value={item.description}
                onChange={(e) => handleLineItemChange(idx, "description", e.target.value)}
                className={`border rounded-lg px-2 py-1 col-span-2 ${formErrors[`description-${idx}`] ? "border-red-500" : ""}`}
                required
              />
              <input
                type="number"
                min="1"
                placeholder="Qty"
                value={item.quantity}
                onChange={(e) => handleLineItemChange(idx, "quantity", e.target.value)}
                className={`border rounded-lg px-2 py-1 ${formErrors[`quantity-${idx}`] ? "border-red-500" : ""}`}
                required
              />
              <input
                type="text"
                placeholder="Unit"
                value={item.unit}
                onChange={(e) => handleLineItemChange(idx, "unit", e.target.value)}
                className={`border rounded-lg px-2 py-1 ${formErrors[`unit-${idx}`] ? "border-red-500" : ""}`}
                required
              />
              
              <input
                type="text"
                placeholder="Remarks"
                value={item.remarks}
                onChange={(e) => handleLineItemChange(idx, "remarks", e.target.value)}
                className="border rounded-lg px-2 py-1"
              />
              {lineItems.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLineItem(idx)}
                  className="text-red-500 hover:text-red-700 ml-2"
                  aria-label="Remove line item"
                >
                  ✖
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addLineItem}
            className="mt-2 px-3 py-1 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm"
          >
            + Add Line Item
          </button>
        </div>

        {/* File Upload */}
        <div>
          <label className="block font-medium mb-1">Attach Files</label>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            accept={ALLOWED_FILE_TYPES.join(",")}
            className="w-full border rounded-lg px-3 py-2"
          />
          {files.length > 0 && (
            <ul className="mt-2 text-sm">
              {files.map((file, idx) => (
                <li key={idx} className="flex justify-between items-center">
                  <span>{file.name}</span>
                  {formErrors[`fileType-${idx}`] && (
                    <span className="text-red-500">{formErrors[`fileType-${idx}`]}</span>
                  )}
                  {formErrors[`fileSize-${idx}`] && (
                    <span className="text-red-500">{formErrors[`fileSize-${idx}`]}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/oa/procurement")}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-white ${
              loading ? "bg-orange-300 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600"
            }`}
          >
            {loading ? "Submitting..." : "Submit RFQ"}
          </button>
        </div>
      </form>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
            <h3 className="text-xl font-semibold mb-4">Confirm RFQ Submission</h3>
            <div className="mb-6">
              <p className="mb-2">Are you sure you want to submit this RFQ?</p>
              <p className="text-sm text-gray-600">
                This will notify <strong>{selectedVendors.length} vendor(s)</strong> via email:
              </p>
              <ul className="mt-2 text-sm text-gray-700 max-h-32 overflow-y-auto">
                {selectedVendors.map((vendor, idx) => (
                  <li key={idx} className="py-1">
                    • {vendor.name} ({vendor.email || vendor.contactEmail || 'No email'})
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Confirm & Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateRFQ;
